import { useCallback, useState } from "react";
import { Button, Input } from "@legal-500/legal500-design-system";
import { PageHeader } from "../../../components/PageHeader/index.js";
import { ConfirmationModal } from "../../../components/Modal/ConfirmationModal.js";
import { CompanyPicker } from "../../../components/CompanyPicker/CompanyPicker.js";
import { AddressBookContactPicker } from "../../../components/Profiles/ListFilters/AddressBookContactPicker.js";
import { usePermissions } from "../../../hooks/usePermissions.js";
import type { Company } from "../../../api/addressBook.service.js";
import {
  addressBookRenameReplayService,
  type RenameReplayEntityType,
  type RenameReplayResponse,
} from "../../../api/addressBookRenameReplay.service.js";
import styles from "./AddressBookRenameReplay.module.css";

type Phase = "idle" | "previewing" | "previewed" | "applying" | "done";

const ENTITY_LABEL: Record<RenameReplayEntityType, string> = {
  company: "firm",
  contact: "lawyer",
};

function entries(count: number): string {
  return `${count} ${count === 1 ? "entry" : "entries"}`;
}

function inputsKey(entityType: RenameReplayEntityType, id: string | null, previousName: string) {
  return JSON.stringify([entityType, id, previousName]);
}

export default function AddressBookRenameReplay() {
  const { hasAllPermissions } = usePermissions();

  const [entityType, setEntityType] = useState<RenameReplayEntityType>("company");
  const [company, setCompany] = useState<Company | null>(null);
  const [contactId, setContactId] = useState<string | null>(null);
  const [previousName, setPreviousName] = useState("");
  const [phase, setPhase] = useState<Phase>("idle");
  const [preview, setPreview] = useState<RenameReplayResponse | null>(null);
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [applied, setApplied] = useState<RenameReplayResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const canUse = hasAllPermissions(["developer.write", "addressbook.read"]);

  const resetResults = useCallback(() => {
    setPreview(null);
    setPreviewKey(null);
    setApplied(null);
    setError(null);
    setPhase("idle");
  }, []);

  const entityId = entityType === "company" ? (company?.id ?? null) : contactId;
  const trimmedPreviousName = previousName.trim();
  const currentKey = inputsKey(entityType, entityId, trimmedPreviousName);

  const runPreview = useCallback(async () => {
    if (!entityId) {
      return;
    }
    resetResults();
    setPhase("previewing");
    try {
      const response = await addressBookRenameReplayService.replay({
        entityType,
        id: entityId,
        previousName: trimmedPreviousName,
        dryRun: true,
      });
      setPreview(response);
      setPreviewKey(currentKey);
      setPhase("previewed");
    } catch (previewError) {
      setError(previewError instanceof Error ? previewError.message : String(previewError));
      setPhase("idle");
    }
  }, [currentKey, entityId, entityType, resetResults, trimmedPreviousName]);

  const runApply = useCallback(async () => {
    setIsConfirming(false);
    if (!entityId) {
      return;
    }
    setError(null);
    setPhase("applying");
    try {
      const response = await addressBookRenameReplayService.replay({
        entityType,
        id: entityId,
        previousName: trimmedPreviousName,
        dryRun: false,
      });
      setApplied(response);
      setPhase("done");
    } catch (applyError) {
      setError(applyError instanceof Error ? applyError.message : String(applyError));
      setPhase("previewed");
    }
  }, [entityId, entityType, trimmedPreviousName]);

  if (!canUse) {
    return (
      <div className={styles.page}>
        <div className={styles.accessDenied} role="alert">
          <h2>Access Denied</h2>
          <p>
            The Address Book Rename Replay tool needs developer access and Address book read access.
          </p>
        </div>
      </div>
    );
  }

  const isBusy = phase === "previewing" || phase === "applying";
  const label = ENTITY_LABEL[entityType];
  const toUpdate = preview?.rowsUpdated ?? 0;
  const canPreview = entityId !== null && trimmedPreviousName.length > 0 && !isBusy;
  const canApply =
    phase === "previewed" && preview !== null && previewKey === currentKey && toUpdate > 0;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Address Book Rename Replay"
        subtitle="Update editorial text in live and researching editions that still shows a firm or lawyer's old name"
      />

      <p className={styles.intro}>
        Use this when a firm or lawyer was renamed in the Address book but some editorial text still
        shows the old name. Pick the firm or lawyer by their current name, enter the old name that
        still appears, and preview what would change. Applying replaces the old name with the
        current Address book name in editorial text for live and researching editions only. Editions
        in setup, editing, ready for release or archived are not changed. Only the original-language
        text is updated; translations are not changed. The Address book itself is not changed.
      </p>

      {error ? (
        <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
          {error}
        </div>
      ) : null}

      <section className={styles.card} aria-labelledby="rename-step-1">
        <h2 className={styles.cardTitle} id="rename-step-1">
          1 · Pick the firm or lawyer
        </h2>
        <fieldset className={styles.typeToggle} disabled={isBusy}>
          <legend className={styles.typeLegend}>Renamed in the Address book</legend>
          {(["company", "contact"] as const).map(option => (
            <label key={option} className={styles.typeOption}>
              <input
                type="radio"
                name="rename-entity-type"
                value={option}
                checked={entityType === option}
                onChange={() => {
                  setEntityType(option);
                  resetResults();
                }}
              />
              <span>{option === "company" ? "Firm" : "Lawyer"}</span>
            </label>
          ))}
        </fieldset>
        <div className={styles.fields}>
          {entityType === "company" ? (
            <CompanyPicker
              value={company}
              onChange={next => {
                setCompany(next);
                resetResults();
              }}
              label="Firm"
              placeholder="Search firms by current name…"
              disabled={isBusy}
              listboxId="rename-replay-firm-listbox"
            />
          ) : (
            <AddressBookContactPicker
              companyId={null}
              companyOptional
              value={contactId}
              onChange={next => {
                setContactId(next);
                resetResults();
              }}
              label="Lawyer"
              placeholder="Search lawyers by current name…"
              disabled={isBusy}
            />
          )}
          <Input
            label="Previous name"
            value={previousName}
            onChange={event => {
              setPreviousName(event.target.value);
              resetResults();
            }}
            placeholder="The old name still showing in live or researching editorial text"
            disabled={isBusy}
            fullWidth
          />
        </div>
        <div className={styles.controls}>
          <Button
            variant="contained"
            color="primary"
            disabled={!canPreview}
            onClick={() => void runPreview()}
          >
            Preview
          </Button>
          <Button variant="outlined" disabled={!canApply} onClick={() => setIsConfirming(true)}>
            Apply
          </Button>
        </div>
        <p className={styles.hint}>
          Preview only reads. Apply becomes available after a preview of exactly these choices, and
          only when there is something to update.
        </p>
      </section>

      {isBusy ? (
        <div className={styles.card} role="status" aria-live="polite">
          <p className={styles.status}>
            {phase === "previewing" ? "Loading preview…" : "Updating editorial text…"}
          </p>
        </div>
      ) : null}

      {preview && !isBusy && phase !== "done" ? (
        <section className={styles.card} aria-labelledby="rename-step-2">
          <h2 className={styles.cardTitle} id="rename-step-2">
            2 · Preview
          </h2>
          <dl className={styles.names}>
            <div className={styles.nameRow}>
              <dt>Current Address book name</dt>
              <dd>{preview.currentName}</dd>
            </div>
            <div className={styles.nameRow}>
              <dt>Previous name</dt>
              <dd>{preview.previousName}</dd>
            </div>
          </dl>
          <div className={styles.tiles}>
            <div className={styles.tile}>
              <div className={styles.tileNumber}>{preview.rowsMatched}</div>
              <div className={styles.tileLabel}>Text entries in live and researching editions</div>
            </div>
            <div
              className={`${styles.tile} ${styles.tileHeadline} ${
                toUpdate > 0 ? styles.tileWarning : styles.tileOk
              }`}
            >
              <div className={styles.tileNumber}>{toUpdate}</div>
              <div className={styles.tileLabel}>Would be updated</div>
            </div>
          </div>
          {toUpdate === 0 ? (
            <p className={styles.hint}>
              {`No editorial text for this ${label} shows "${preview.previousName}", so there is nothing to update.`}
            </p>
          ) : null}
        </section>
      ) : null}

      {phase === "done" && applied ? (
        <section className={styles.card} aria-labelledby="rename-step-3">
          <h2 className={styles.cardTitle} id="rename-step-3">
            3 · Applied
          </h2>
          <div className={styles.tiles}>
            <div className={`${styles.tile} ${styles.tileOk}`}>
              <div className={styles.tileNumber}>{applied.rowsUpdated}</div>
              <div className={styles.tileLabel}>Updated</div>
            </div>
          </div>
          <p className={styles.hint}>
            Run Preview again to check: the number that would be updated should now be 0.
          </p>
        </section>
      ) : null}

      {isConfirming && preview ? (
        <ConfirmationModal
          title="Update editorial text"
          message={
            `${entries(toUpdate)} of editorial text will change "${preview.previousName}" to ` +
            `"${preview.currentName}".`
          }
          warningMessage=""
          confirmButtonText={`Update ${entries(toUpdate)}`}
          backdropClick={false}
          onClose={() => setIsConfirming(false)}
          onConfirm={runApply}
        />
      ) : null}
    </div>
  );
}
